import { type CSSProperties, type ReactNode } from 'react';
import { type RequestContext } from '@cx-web/domain-types';

import { type BettingInsightsContent, type EsIndex } from '../types';

// Type for the context that will be passed as first parameter to render functions
export interface BettingInsightsMainContext {
  environment: RequestContext['environment'];
  featureFlags: RequestContext['featureFlags'];
  repubConfig: RequestContext['repubConfig'];
  user: RequestContext['user'];
}

export interface BettingInsightsMainConfig<TIndex extends EsIndex> {
  alignItems?: CSSProperties['alignItems'];
  render: (
    context: BettingInsightsMainContext,
    data: {
      content: BettingInsightsContent<TIndex>;
      resourcesUrl: string;
      resultIndex: number;
    },
  ) => ReactNode;
}

// Type alias for the return type of createBettingInsightsMain
export type BettingInsightsMain<TIndex extends EsIndex> = Readonly<{
  alignItems?: CSSProperties['alignItems'];
  render: (data: { content: BettingInsightsContent<TIndex>; resourcesUrl: string; resultIndex: number }) => ReactNode;
}>;

/**
 * Factory function to create BettingInsightsMain configuration (functional approach)
 * @param requestContext - The request context
 * @param config - The main configuration
 * @returns Immutable main configuration object
 */
export function createBettingInsightsMain<TIndex extends EsIndex>(
  requestContext: RequestContext,
  config: BettingInsightsMainConfig<TIndex>,
): BettingInsightsMain<TIndex> {
  // Create context object
  const context: BettingInsightsMainContext = {
    environment: requestContext.environment,
    featureFlags: requestContext.featureFlags,
    repubConfig: requestContext.repubConfig,
    user: requestContext.user,
  };

  // Create the result object with wrapper function that injects context
  const result = {
    // Config properties
    alignItems: config.alignItems,

    // Wrapper function that injects context as first parameter
    render: (data: { content: BettingInsightsContent<TIndex>; resourcesUrl: string; resultIndex: number }) =>
      config.render(context, data),
  };

  return Object.freeze(result);
}
